import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { stageForPath, trackEvent, trackFunnelStage, trackNavClick, trackOutbound, trackResourceDownload } from "@/lib/telemetry";

const DOWNLOAD_EXT = /\.(pdf|docx?|pptx?|xlsx?|csv|zip)$/i;

/** Hosts whose links are clinical/scientific sources rather than general outbound. */
const CLINICAL_SOURCE_HOSTS = [
  "pubmed.ncbi.nlm.nih.gov",
  "ncbi.nlm.nih.gov",
  "ahajournals.org",
  "nejm.org",
  "jamanetwork.com",
  "thelancet.com",
  "bmj.com",
  "resuscitationjournal.com",
  "cpr.heart.org",
  "heart.org",
  "who.int",
  "fda.gov",
  "nih.gov",
  "doi.org",
];

/**
 * Sitewide, privacy-conscious behavioural analytics:
 *  - a page_view per route (path only, never the query string);
 *  - the conversion funnel stage the route represents;
 *  - delegated link telemetry for navigation paths, outbound clicks, clinical
 *    source citations, and document downloads.
 *
 * No form values, search terms, or personal data are read here.
 */
export const RouteAnalytics = () => {
  const { pathname } = useLocation();
  const previous = useRef<string | null>(null);

  useEffect(() => {
    trackEvent("page_view", pathname, previous.current ? { from_path: previous.current } : undefined);
    const stage = stageForPath(pathname);
    if (stage) trackFunnelStage(stage);
    previous.current = pathname;
  }, [pathname]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.("a[href]") as
        | HTMLAnchorElement
        | null;
      if (!anchor) return;

      const href = anchor.getAttribute("href") ?? "";
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return;

      const label = (anchor.getAttribute("aria-label") || anchor.textContent || "").trim();

      if (/^https?:\/\//i.test(href)) {
        let host = "";
        try {
          host = new URL(href).host;
        } catch {
          return;
        }
        if (host === window.location.host) return;
        trackOutbound(href, CLINICAL_SOURCE_HOSTS.some((h) => host === h || host.endsWith(`.${h}`)));
        return;
      }

      if (DOWNLOAD_EXT.test(href)) {
        trackResourceDownload(href.split("/").pop() ?? href);
        return;
      }

      // Internal navigation originating from the header, footer, or breadcrumbs.
      const surface = anchor.closest("header")
        ? "header"
        : anchor.closest("footer")
          ? "footer"
          : anchor.closest("nav[aria-label='Breadcrumb']")
            ? "breadcrumb"
            : null;
      if (surface && label) trackNavClick(`${surface}:${label}`, window.location.pathname);
    };

    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true } as EventListenerOptions);
  }, []);

  return null;
};
